<template>
  <jet-action-section>
    <template #title>Browser sessions</template>

    <template #description>
      Manage and log out of active sessions on other browsers and devices. You can log out of all
      sessions across all devices. Recent sessions are listed below, though this may not be
      exhaustive. If you suspect a compromise, update your password immediately.
    </template>

    <template #content>
      <!-- Other Browser Sessions -->
      <div class="mt-8 space-y-6" v-if="sessions.length > 0">
        <div class="flex items-center" v-for="(session, i) in sessions" :key="i">
          <div>
            <svg
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              class="w-8 h-8 text-vice-teal-600"
              v-if="session.agent.is_desktop"
            >
              <path
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2
                0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              ></path>
            </svg>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="w-8 h-8 text-gray-500"
              v-else
            >
              <path d="M0 0h24v24H0z" stroke="none"></path>
              <rect x="7" y="4" width="10" height="16" rx="1"></rect>
              <path d="M11 5h2M12 17v.01"></path>
            </svg>
          </div>

          <div class="ml-8">
            <div class="text-sm text-gray-600">
              {{ session.agent.platform }} - {{ session.agent.browser }} -
              {{ session.ip_address }} -
              <span class="text-vice-teal-600 text-xxs font-light" v-if="session.is_current_device">
                This device
              </span>
              <span v-else class="text-vice-teal-600 text-xxs font-light">
                Last active {{ session.last_active }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center mt-8 mb-2">
        <ViceButton
          @click="confirmLogout"
          data-cy="user-loob"
          class="px-4 border border-vice-light-grey"
          :class="{ 'opacity-25': form.processing }"
          :disabled="form.processing"
          medium
        >
          Log out other browser sessions
        </ViceButton>

        <action-message
          :on="form.recentlySuccessful"
          message="You've been logged out of other browser sessions."
        ></action-message>
      </div>

      <!-- Log Out Other Devices Confirmation Modal -->
      <jet-dialog-modal :show="confirmingLogout" @close="closeModal">
        <template #title>
          <span class="text-vice-teal-900">Log out other browser sessions</span>
        </template>

        <template #content>
          <span class="text-vice-teal-900">
            Please enter your password to confirm you would like to log out of your other browser
            sessions across all of your devices.
          </span>

          <div class="mt-4">
            <jet-input
              data-cy="user-loob-password"
              type="password"
              class="mt-1 block w-3/4"
              placeholder="Password"
              ref="password"
              v-model="form.password"
              @keyup.enter="logoutOtherBrowserSessions"
            />

            <jet-input-error :message="form.errors.password" class="mt-2" />
          </div>
        </template>

        <template #footer>
          <jet-secondary-button data-cy="user-loob-cancel" @click="closeModal">
            Cancel
          </jet-secondary-button>

          <ViceButton
            data-cy="user-loob-confirm"
            @click="logoutOtherBrowserSessions"
            :class="{ 'opacity-25': form.processing }"
            :disabled="form.processing"
            class="ml-2 px-4 border border-vice-light-grey"
            medium
          >
            Log out other browser sessions
          </ViceButton>
        </template>
      </jet-dialog-modal>
    </template>
  </jet-action-section>
</template>

<script>
import ActionMessage from '@/Jetstream/ActionMessage.vue';
import JetActionSection from '@/Jetstream/ActionSection.vue';
import JetDialogModal from '@/Jetstream/DialogModal.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';

export default {
  props: ['sessions'],

  components: {
    ViceButton,
    ActionMessage,
    JetActionSection,
    JetDialogModal,
    JetInput,
    JetInputError,
    JetSecondaryButton,
  },

  data() {
    return {
      confirmingLogout: false,

      form: this.$inertia.form({
        password: '',
      }),
    };
  },

  methods: {
    confirmLogout() {
      this.confirmingLogout = true;

      setTimeout(() => this.$refs.password.focus(), 250);
    },

    logoutOtherBrowserSessions() {
      const routeName = this.$page.props.isClinician
        ? 'clinician.other-browser-sessions.destroy'
        : 'other-browser-sessions.destroy';
      this.form.delete(route(routeName), {
        preserveScroll: true,
        onSuccess: () => this.closeModal(),
        onError: () => this.$refs.password.focus(),
        onFinish: () => this.form.reset(),
      });
    },

    closeModal() {
      this.confirmingLogout = false;

      this.form.reset();
      this.form.errors.password = '';
    },
  },
};
</script>
